/**
 * HULL WALK — NavalArch101 chapter 1. Eight ships, and the type is the question.
 *
 * FOUNDER, 2026-08-20: "now do the walkthrough for the other ships too".
 *
 * ── WHY THIS IS NOT THE DP WALK POINTED AT MORE SHIPS ─────────────────────────────────────────
 * Measured first: one of the eight declares a DP system. The DP walk goes switchboard → sets →
 * thrusters → the bulkhead between the halves, and the other seven have none of those. Running it
 * over them would have meant inventing a redundancy chain, which is the fabrication this estate is
 * built against wearing the costume of a request being honoured.
 *
 * ── AND THE RIGHT WALK WAS ALREADY WRITTEN AS A CHAPTER ───────────────────────────────────────
 * "A Ship Is Eight Ships" argues that seven of them are the same length and the TYPE is what
 * differs. That is true of the geometry, exactly: nine parts are common to all eight, and
 * everything else is what she carries — hoppers and topsides, an inner hull and longitudinal
 * bulkheads, a torsion box and cell guides, vehicle decks and a stern ramp, four decks and fire
 * zones. So the lab climbs each ship keel-up through her OWN declared layers and marks what is
 * hers alone. The chapter's claim stops being a claim.
 *
 * ── ONE RENDERER ──────────────────────────────────────────────────────────────────────────────
 * The same shipwalk3d that draws the DP walk. It was extracted from that file before this one
 * could copy it: two renderers drawing the same declared parts is the drift fault this app already
 * guards between its browser and server engines.
 */
import { useState, useEffect, useRef } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { makeWalk } from './shipwalk3d.mjs';
import { hullStations, sharedParts, distinctiveParts, fleetShape, WHY_NOT_THE_DP_WALK, SOURCE_NA1, TYPE_IS_THE_QUESTION } from './hullwalk.mjs';

const FLEET_URL = '/api/ship/fleet';
const THREE_URL = '/vendor/three.module.min.js';
const REALISTIC_URL = '/dp-realistic.mjs';

export default function HullWalkLab() {
  const box = useRef(null);
  const engine = useRef(null);
  const [models, setModels] = useState(null);
  const [ship, setShip] = useState(null);
  const [step, setStep] = useState(0);
  const [err, setErr] = useState(null);
  const [parity, setParity] = useState(null);

  /* Every ship is fetched, because the chapter's claim is a claim ABOUT THE FLEET — what is common
     to all eight cannot be computed from one of them. A hardcoded list of ship keys here would be
     the hand-kept map again, so the fleet is asked what it contains. */
  useEffect(() => {
    let live = true;
    fetch(FLEET_URL).then((r) => r.json())
      .then(async (f) => {
        const keys = f.ships || f.fleet || Object.keys(f);
        const ms = await Promise.all(keys.map((k) => fetch(`/api/ship/model?ship=${k}`).then((r) => r.json())));
        if (!live) return;
        setModels(ms.filter((m) => m && m.parts));
        setShip((ms[0] || {}).ship || null);
      })
      .catch((e) => live && setErr(`the fleet could not be read (${e})`));
    return () => { live = false; };
  }, []);

  const m3 = models && ship ? models.find((m) => m.ship === ship) : null;
  const shared = models ? sharedParts(models) : null;
  const walk = m3 && shared ? hullStations(m3, shared) : null;
  const here = walk ? walk.stations[Math.min(step, walk.stations.length - 1)] : null;
  const fleet = models ? fleetShape(models) : null;

  useEffect(() => { setStep(0); }, [ship]);

  useEffect(() => {
    if (!m3 || !box.current) return;
    let live = true;
    (async () => {
      try {
        const [THREE, R] = await Promise.all([import(/* @vite-ignore */ THREE_URL), import(/* @vite-ignore */ REALISTIC_URL)]);
        if (!live) return;
        const g = R.geometryParity(m3);
        setParity(g);
        if (!g.ok) { setErr(`${m3.vessel}'s model does not match its own declaration (declared ${g.declared}, counted ${g.counted})`); return; }
        if (engine.current) engine.current.dispose();
        engine.current = makeWalk(THREE, box.current, m3);
      } catch (e) { if (live) setErr(`the renderer is unavailable here (${e && e.message ? e.message : e})`); }
    })();
    return () => { live = false; };
  }, [m3 && m3.ship]);

  useEffect(() => { if (engine.current && here) engine.current.goTo(here, new Set()); }, [step, here && here.id]);
  useEffect(() => () => { if (engine.current) { engine.current.dispose(); engine.current = null; } }, []);

  if (err) return <div className="g-glass shell-panel lab"><p className="g-body"><Value of={refused(`Not shown: ${err}. Nothing is drawn rather than an approximate ship.`)} /></p></div>;
  if (!models || !m3) return <div className="g-glass shell-panel lab"><p className="g-body g-dim">Loading the fleet…</p></div>;

  const own = distinctiveParts(m3, shared);

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Hull walk · eight ships, keel up</div>
      <h2 className="g-h2 lab-h">Seven of them are the same length.</h2>

      <p className="g-body"><Value of={cited(TYPE_IS_THE_QUESTION, SOURCE_NA1)} /></p>
      {fleet && (
        <p className="g-body">
          <Value of={computed(`${fleet.same_length_count} of ${models.length} are ${fleet.same_length_m} m`)} />{' '}
          and <Value of={computed(`${fleet.shared.length} parts are common to all ${models.length}`)} />.
          Everything else is what she carries.
        </p>
      )}

      <div className="lab-task">
        <div className="g-label">Choose a ship</div>
        <div className="lab-task-head dpf-picks">
          {models.map((m) => (
            <button key={m.ship} className={`g-btn ${m.ship === ship ? 'dpb-on' : ''}`} onClick={() => setShip(m.ship)}>{m.vessel}</button>
          ))}
        </div>
        <p className="g-body g-dim">{m3.type}</p>
      </div>

      <div ref={box} className="dpw-canvas" />

      {walk && (
        <>
          <div className="lab-task-head dpf-picks">
            {walk.stations.map((s, i) => (
              <button key={s.id} className={`g-btn ${i === step ? 'dpb-on' : ''} ${s.distinctive && s.distinctive.length ? 'dpf-worst' : ''}`} onClick={() => setStep(i)}>{s.name}</button>
            ))}
          </div>
          {here && (
            <>
              <p className="g-body dpw-says"><b>{here.name}.</b> {here.says}</p>
              {here.distinctive && here.distinctive.length > 0 && (
                <p className="g-body"><Value of={computed(`hers alone: ${here.distinctive.join(', ')}`)} /></p>
              )}
            </>
          )}
          {walk.orphanLayers.length > 0 && (
            <p className="g-body"><Value of={refused(`Parts claim layers her model does not list: ${walk.orphanLayers.join(', ')}. The two declarations disagree.`)} /></p>
          )}
        </>
      )}

      {/* ── WHAT MAKES HER HER ── */}
      <div className="lab-task dpb-verdict">
        <div className="g-label">What {m3.vessel} has that the common hull does not</div>
        <p className="g-body">
          <Value of={computed(`${own.length} part(s)`)} />: {own.map((p) => p.id).join(', ')}
        </p>
        <p className="g-body g-dim">
          Every ship here has a shell, a double bottom with its tank top, centre girder and solid
          floors, transverse bulkheads, an accommodation block and a funnel. Those nine are the hull
          that is a hull whatever she carries. The rest is the answer to “what is she for?”
        </p>
      </div>

      {/* ── WHY THE DP WALK IS NOT THIS WALK ── */}
      <div className="lab-task">
        <div className="g-label">Why this is not the DP walk</div>
        <p className="g-body g-dim">{WHY_NOT_THE_DP_WALK}</p>
      </div>

      {/* ── WHAT HER MODEL SAYS IT IS NOT ── */}
      <div className="dpw-honesty">
        <div className="g-label">What this model is not</div>
        {parity && <p className="g-body"><Value of={computed(`${parity.counted} quads drawn, ${parity.declared} declared — parity holds`)} /></p>}
        {(m3.known_not_drawn || []).map((s, i) => <p key={`k${i}`} className="g-body"><Value of={absent()} /> {s}</p>)}
        {(m3.not_modelled || []).map((s, i) => <p key={`n${i}`} className="g-body g-dim">{s}</p>)}
        {m3.source && <p className="g-body g-dim"><b>Source:</b> {m3.source}</p>}
        {!(m3.known_not_drawn || []).length && !(m3.not_modelled || []).length && (
          <p className="g-body"><Value of={refused('This model declares no limits of its own. Every other model in the fleet does — a 3D view that does not say what it omits is the least accountable surface in the course.')} /></p>
        )}
      </div>
    </div>
  );
}
